import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, CheckCircle, AlertTriangle } from 'lucide-react';
import { formatDisplayDate } from '../../../../../../utils/voucherHelpers';
import {
  getAssetUrl,
  getDestinationsList,
  getTripDates,
  getPartnerVisibilityText
} from '../../utils/dashboardHelpers';

const VoucherHeroSection = ({
  voucher,
  guests = [],
  id,
  isDownloading,
  onDownload,
  onViewItinerary,
  onCopyInviteMessage,
  onWhatsAppShare
}) => {
  const navigate = useNavigate();
  const [showAllDestinations, setShowAllDestinations] = useState(false);
  const [showAttachedMenu, setShowAttachedMenu] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showMobileAttachedMenu, setShowMobileAttachedMenu] = useState(false);
  const [showMobileShareMenu, setShowMobileShareMenu] = useState(false);
  const [showAppLoginTooltip, setShowAppLoginTooltip] = useState(false);
  const attachedMenuRef = useRef(null);
  const shareMenuRef = useRef(null);
  const mobileAttachedMenuRef = useRef(null);
  const mobileShareMenuRef = useRef(null);
  const appLoginTooltipRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (attachedMenuRef.current && !attachedMenuRef.current.contains(event.target)) {
        setShowAttachedMenu(false);
      }
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
      if (mobileAttachedMenuRef.current && !mobileAttachedMenuRef.current.contains(event.target)) {
        setShowMobileAttachedMenu(false);
      }
      if (mobileShareMenuRef.current && !mobileShareMenuRef.current.contains(event.target)) {
        setShowMobileShareMenu(false);
      }
      if (appLoginTooltipRef.current && !appLoginTooltipRef.current.contains(event.target)) {
        setShowAppLoginTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const hasGuests =
    (Array.isArray(guests) && guests.length > 0) ||
    (Array.isArray(voucher?.active_guests) && voucher.active_guests.length > 0);

  const attachedFiles = (voucher?.source_documents || [])
    .map(d => ({ ...d, label: d.filename || d.name }))
    .filter(d => d.url || d.label);

  const dests = getDestinationsList(voucher);

  // Countdown calculations
  const calculateCountdown = () => {
    const startDate = voucher?.tour_info?.start_date;
    const endDate = voucher?.tour_info?.end_date;
    if (!startDate) return { num: '-', status: 'days to go', progText: '' };

    const now = new Date();
    let start = new Date(formatDisplayDate(startDate));
    start.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);

    let diffDays = Math.ceil((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    let status = "days to go";
    let num = diffDays;
    let progText = "Upcoming";

    if (isNaN(num)) {
      num = "-";
      progText = "";
    } else if (diffDays === 0) {
      num = "0";
      status = "Starts Today";
      progText = "On trip";
    } else if (diffDays < 0) {
      if (endDate) {
        let end = new Date(formatDisplayDate(endDate));
        end.setHours(0, 0, 0, 0);
        let diffEnd = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (diffEnd > 0) {
          num = diffEnd;
          status = "days left";
          progText = "On trip";
        } else if (diffEnd === 0) {
          num = "0";
          status = "Ends Today";
          progText = "On trip";
        } else {
          num = Math.abs(diffEnd);
          status = "days ago";
          progText = "Completed";
        }
      } else {
        num = Math.abs(diffDays);
        status = "days ago";
        progText = "On trip / Completed";
      }
    }
    return { num, status, progText };
  };

  const { num, status, progText } = calculateCountdown();

  return (
    <>
      {/* Page Header Bar */}
      <div className="vc-page-head">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0 w-full sm:w-auto">
          <button
            onClick={() => navigate('/voucher')}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white border border-[#e7ecea] flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-[#067978] transition-colors shadow-sm shrink-0 cursor-pointer"
            title="Back to vouchers"
          >
            <ArrowLeft size={18} strokeWidth={2.5} />
          </button>
          <div className="min-w-0 flex-1">
            <h1>Tour Dashboard</h1>
            <p>Everything for this trip — booked, live, and in-app — in one place.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
          <button
            className="vc-btn flex-1 sm:flex-initial justify-center"
            onClick={() => navigate(`/voucher-edit/${id}`)}
          >
            Edit
          </button>
          <button
            className="vc-btn !hidden sm:!inline-flex justify-center"
            onClick={onViewItinerary}
          >
            View Itinerary
          </button>
          <button
            className="vc-btn vc-btn-primary flex-1 sm:flex-initial justify-center"
            onClick={onDownload}
            disabled={isDownloading}
          >
            {isDownloading ? "Downloading..." : "Download Voucher"}
          </button>
        </div>
      </div>

      {/* Hero Banner Card */}
      <div className="vc-hero">
        <div className="vc-hero-main">
          <div className="vc-hero-who flex-wrap">
            <div className="vc-avatar shrink-0">
              {voucher?.guest_information?.guest_name
                ? voucher.guest_information.guest_name.substring(0, 2).toUpperCase()
                : 'GS'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <h2 className="break-words">{voucher?.guest_information?.guest_name || "Guest"}</h2>

                {/* Mobile (< 640px): Warning icon with tap tooltip */}
                <div className="relative inline-flex items-center sm:hidden" ref={appLoginTooltipRef}>
                  <button
                    type="button"
                    onClick={() => setShowAppLoginTooltip(prev => !prev)}
                    className="p-1 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 transition-all cursor-pointer focus:outline-none flex items-center justify-center"
                    title={!hasGuests ? "App Yet to be logged in by Traveller" : "App Logged In"}
                  >
                    {!hasGuests ? (
                      <AlertTriangle size={15} className="text-amber-300 drop-shadow-xs" />
                    ) : (
                      <CheckCircle size={15} className="text-[#a4f1d0] drop-shadow-xs" />
                    )}
                  </button>
                  {showAppLoginTooltip && (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2.5 py-1 bg-slate-900/95 text-white text-[11px] font-semibold rounded-lg shadow-xl w-max max-w-[160px] text-center leading-snug z-50 pointer-events-none border border-white/15">
                      {!hasGuests ? "App Yet to be logged in by Traveller" : "App Logged In"}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900/95" />
                    </div>
                  )}
                </div>

                {/* Desktop & Tablet (>= 640px): Full Status Badge */}
                {!hasGuests ? (
                  <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[11px] sm:text-xs font-medium bg-white/15 text-white border border-white/25 shadow-xs backdrop-blur-md">
                    <AlertTriangle size={12} className="text-amber-300 shrink-0" />
                    App Yet to be logged in by Traveller
                  </span>
                ) : (
                  <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[11px] sm:text-xs font-medium bg-white/15 text-white border border-white/25 shadow-xs backdrop-blur-md">
                    <CheckCircle size={12} className="text-[#a4f1d0] shrink-0" />
                    App Logged In
                  </span>
                )}
              </div>
              <div className="vc-pax">
                👥 {voucher?.guest_information?.total_guests_adults || 0} adults · {voucher?.guest_information?.total_guests_children || 0} children
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4 mt-3">
            <div className="vc-fact min-w-0">
              <div className="vc-lab">Tour</div>
              <div className="vc-val">
                {dests.length <= 2 ? (
                  dests.join(', ') || "N/A"
                ) : (
                  <span>
                    {showAllDestinations ? dests.join(', ') : `${dests.slice(0, 2).join(', ')}...`}
                    <button
                      onClick={() => setShowAllDestinations(!showAllDestinations)}
                      className="text-[#9fe7c0] text-xs ml-1.5 hover:underline focus:outline-none cursor-pointer"
                    >
                      {showAllDestinations ? "less" : `+${dests.length - 2}`}
                    </button>
                  </span>
                )}
              </div>
            </div>

            <div className="vc-fact min-w-0">
              <div className="vc-lab">Dates</div>
              <div className="vc-val">{getTripDates(voucher) || "N/A"}</div>
            </div>

            <div className="vc-fact min-w-0">
              <div className="vc-lab">Preferred Language</div>
              <div className="vc-val capitalize">{voucher?.guest_information?.preferred_language || "English"}</div>
            </div>

            <div className="vc-fact min-w-0">
              <div className="vc-lab">Partner Visibility</div>
              <div className="vc-val">{getPartnerVisibilityText(voucher)}</div>
            </div>

            {attachedFiles.length > 0 && (
              <div className="vc-hero-desktop-attached vc-fact min-w-0 col-span-2 md:col-span-4 relative">
                <div className="vc-lab">Attached Files</div>
                <div className="relative inline-block mt-0.5" ref={attachedMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowAttachedMenu(prev => !prev)}
                    className="flex items-center gap-2 bg-white/15 border border-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-white/25 active:scale-95 transition-all cursor-pointer shadow-sm"
                  >
                    {attachedFiles.length} {attachedFiles.length === 1 ? 'FILE' : 'FILES'}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${showAttachedMenu ? 'rotate-180' : ''}`} />
                  </button>
                  {showAttachedMenu && (
                    <div className="absolute left-0 top-full mt-2 w-64 max-w-[calc(100vw-36px)] bg-white rounded-xl shadow-2xl border border-[#e7ecea] z-50 overflow-hidden py-1">
                      {attachedFiles.map((doc, idx) => (
                        <a
                          key={idx}
                          href={getAssetUrl(doc.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setShowAttachedMenu(false)}
                          className="block w-full text-left px-4 py-2.5 text-[13px] text-[#374151] hover:bg-slate-50 hover:text-[#067978] font-semibold transition-colors border-b border-[#e7ecea] last:border-0 truncate"
                          title={doc.label || `File ${idx + 1}`}
                        >
                          📄 {doc.label || `File ${idx + 1}`}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Classic Booking ID & Countdown (screens >= 640px) */}
        <div className="vc-hero-desktop-right flex-col sm:flex-row lg:flex-col items-stretch sm:items-center lg:items-end justify-between lg:justify-start gap-2.5 sm:gap-4 shrink-0 pt-3 lg:pt-0 border-t border-white/15 lg:border-t-0 w-full lg:w-auto">
          <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-3 bg-white/10 border border-white/15 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl sm:rounded-2xl shadow-inner backdrop-blur-sm">
            <span className="text-[10px] font-bold text-[#a4f1d0] uppercase tracking-wider">Booking ID:</span>
            <span className="font-mono text-white text-xs sm:text-sm">{voucher?.booking_id || "N/A"}</span>
            <div className="relative ml-1 sm:ml-2" ref={shareMenuRef}>
              <button
                type="button"
                onClick={() => setShowShareMenu(prev => !prev)}
                className="flex items-center gap-1 bg-white/15 hover:bg-white/25 active:scale-95 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded transition-all border border-white/20 shadow-sm cursor-pointer"
              >
                Share <ChevronDown size={11} strokeWidth={3} className={`transition-transform duration-200 ${showShareMenu ? 'rotate-180' : ''}`} />
              </button>
              {showShareMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 max-w-[calc(100vw-36px)] bg-white rounded-xl shadow-2xl border border-[#e7ecea] z-50 overflow-hidden py-1">
                  <button
                    type="button"
                    onClick={() => {
                      onCopyInviteMessage?.();
                      setShowShareMenu(false);
                    }}
                    className="w-full text-left px-4 py-3 text-[13px] text-[#374151] hover:bg-slate-50 hover:text-[#067978] font-semibold transition-colors flex items-center gap-3 cursor-pointer"
                  >
                    <span className="text-lg leading-none">⧉</span> Copy message
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onWhatsAppShare?.();
                      setShowShareMenu(false);
                    }}
                    className="w-full text-left px-4 py-3 text-[13px] text-[#374151] hover:bg-slate-50 hover:text-[#25D366] font-semibold transition-colors flex items-center gap-3 border-t border-[#e7ecea] cursor-pointer"
                  >
                    <span className="text-lg leading-none text-[#25D366]">💬</span> WhatsApp
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="vc-countdown">
            <div className="vc-num">{num}</div>
            <div className="vc-unit">{status}</div>
            <div className="vc-prog">{progText}</div>
          </div>
        </div>

        {/* Mobile View: 3 Equal-Height Stat Boxes in a Row (< 640px) */}
        <div className="vc-hero-mobile-3boxes w-full shrink-0 pt-3 border-t border-white/10 mt-1">
          <div className="grid grid-cols-3 items-stretch gap-1.5 sm:gap-2 w-full">
            {/* Card 1: Booking ID */}
            <div className="bg-white/10 rounded-2xl p-2 sm:p-2.5 text-center relative box-border flex flex-col items-center justify-between min-w-0">
              <div className="h-4 flex items-center justify-center text-[8.5px] sm:text-[9.5px] font-bold uppercase tracking-wider text-white/75 whitespace-nowrap">
                Booking ID
              </div>
              <div className="flex-1 flex items-center justify-center my-0.5 w-full min-w-0">
                <span className="font-mono text-white text-xs sm:text-sm font-bold tracking-tight truncate max-w-full px-1">
                  {voucher?.booking_id || "N/A"}
                </span>
              </div>
              <div className="h-6 flex items-center justify-center relative w-full" ref={mobileShareMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowMobileShareMenu(prev => !prev)}
                  className="h-5 flex items-center gap-1 bg-white/15 hover:bg-white/25 active:scale-95 text-white text-[9px] font-bold uppercase tracking-wider px-2 rounded-md transition-all cursor-pointer shadow-2xs"
                >
                  Share <ChevronDown size={10} strokeWidth={3} className={`transition-transform duration-200 ${showMobileShareMenu ? 'rotate-180' : ''}`} />
                </button>
                {showMobileShareMenu && (
                  <div className="absolute left-0 top-full mt-2 w-48 max-w-[calc(100vw-36px)] bg-white rounded-xl shadow-2xl border border-[#e7ecea] z-50 overflow-hidden py-1 text-left">
                    <button
                      type="button"
                      onClick={() => {
                        onCopyInviteMessage?.();
                        setShowMobileShareMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 text-[13px] text-[#374151] hover:bg-slate-50 hover:text-[#067978] font-semibold transition-colors flex items-center gap-3 cursor-pointer"
                    >
                      <span className="text-lg leading-none">⧉</span> Copy message
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onWhatsAppShare?.();
                        setShowMobileShareMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 text-[13px] text-[#374151] hover:bg-slate-50 hover:text-[#25D366] font-semibold transition-colors flex items-center gap-3 border-t border-[#e7ecea] cursor-pointer"
                    >
                      <span className="text-lg leading-none text-[#25D366]">💬</span> WhatsApp
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Card 2: Attached Files */}
            <div className="bg-white/10 rounded-2xl p-2 sm:p-2.5 text-center relative box-border flex flex-col items-center justify-between min-w-0">
              <div className="h-4 flex items-center justify-center text-[8.5px] sm:text-[9.5px] font-bold uppercase tracking-wider text-white/75 whitespace-nowrap">
                Attached
              </div>
              <div className="flex-1 flex items-center justify-center my-0.5 w-full min-w-0">
                <span className="text-white text-xs sm:text-sm font-bold leading-none">
                  {attachedFiles.length} <span className="text-[8.5px] sm:text-[9.5px] font-semibold uppercase text-white/80">{attachedFiles.length === 1 ? 'File' : 'Files'}</span>
                </span>
              </div>
              <div className="h-6 flex items-center justify-center relative w-full" ref={mobileAttachedMenuRef}>
                {attachedFiles.length > 0 ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowMobileAttachedMenu(prev => !prev)}
                      className="h-5 flex items-center gap-1 bg-white/15 hover:bg-white/25 active:scale-95 text-white text-[9px] font-bold uppercase tracking-wider px-2 rounded-md transition-all cursor-pointer shadow-2xs"
                    >
                      View <ChevronDown size={10} strokeWidth={3} className={`transition-transform duration-200 ${showMobileAttachedMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {showMobileAttachedMenu && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-60 max-w-[calc(100vw-36px)] bg-white rounded-xl shadow-2xl border border-[#e7ecea] z-50 overflow-hidden py-1 text-left">
                        {attachedFiles.map((doc, idx) => (
                          <a
                            key={idx}
                            href={getAssetUrl(doc.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setShowMobileAttachedMenu(false)}
                            className="block w-full text-left px-4 py-2.5 text-[13px] text-[#374151] hover:bg-slate-50 hover:text-[#067978] font-semibold transition-colors border-b border-[#e7ecea] last:border-0 truncate"
                            title={doc.label || `File ${idx + 1}`}
                          >
                            📄 {doc.label || `File ${idx + 1}`}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <span className="h-5 inline-flex items-center text-[9px] font-bold uppercase tracking-wider text-white/50 px-2">
                    None
                  </span>
                )}
              </div>
            </div>

            {/* Card 3: Countdown / Trip Status */}
            <div className="bg-white/10 rounded-2xl p-2 sm:p-2.5 text-center box-border flex flex-col items-center justify-between min-w-0">
              <div className="w-full flex-1 flex flex-col items-center justify-center my-0.5">
                <span className="text-lg sm:text-xl font-black text-white leading-none tracking-tight">
                  {num}
                </span>
                <span className="text-[8px] sm:text-[9px] font-bold uppercase tracking-wider text-white/75 mt-0.5 whitespace-nowrap">
                  {status}
                </span>
              </div>
              <div className="h-6 flex items-center justify-center w-full">
                <span className="h-5 inline-flex items-center text-[8px] sm:text-[9px] font-extrabold uppercase tracking-wider text-[#a4f1d0] px-1.5 rounded-md bg-white/15">
                  {progText || 'Status'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VoucherHeroSection;
